import { DuplicateIcon } from '@panwds/icons';
import { classNames } from '@panwds/tailwindcss';
import React, { forwardRef, useState, useRef, useEffect } from 'react';
import { defineMessage, useIntl } from 'react-intl';
import { copy } from '../../utils/clipboard/clipboard.esm.js';
import '@popperjs/core';
import { Tooltip } from '../Tooltip/Tooltip.esm.js';
import { Button } from './Button.esm.js';

const defaultProps = {
    appearance: 'tertiary-clear',
    iconSize: 'md',
    size: 'md',
    disabled: false
};
const copyMessage = defineMessage({
    defaultMessage: 'Copy to clipboard',
    id: '2YgpgP',
    description: 'Copy the associated text to the clipboard'
});
const copiedMessage = defineMessage({
    defaultMessage: 'Copied!',
    id: 'XfyhUA',
    description: 'Indicates the associated text has been copied to the clipboard'
});
/**
 * A button that copies the provided `text` to the clipboard when clicked`.
 */
const CopyButton = forwardRef(({ addClassName, appearance = 'tertiary-clear', iconSize = 'sm', size, onClick, text, ...passThrough }, ref) => {
    const [hasCopied, setHasCopied] = useState(false);
    const timeout = useRef();
    const intl = useIntl();
    const message = hasCopied ? copiedMessage : copyMessage;
    const label = intl.formatMessage(message);
    useEffect(() => {
        if (hasCopied) {
            timeout.current = setTimeout(() => {
                setHasCopied(false);
            }, 2000);
        }
        return () => {
            if (timeout.current) {
                clearTimeout(timeout.current);
            }
        };
    }, [hasCopied]);
    return (React.createElement(Tooltip, { label: label },
        React.createElement(Button, { addClassName: classNames('tw-items-center', addClassName), "aria-label": label, appearance: appearance, icon: React.createElement(DuplicateIcon, { size: iconSize, className: "tw-absolute" }), onMouseDown: () => {
                // Set copied on the initial mouse down to avoid the tooltip from unmounting and mounting again
                setHasCopied(true);
            }, onClick: (e) => {
                // Set copied again, in case the button was "clicked" with the keyboard
                setHasCopied(true);
                onClick === null || onClick === void 0 ? void 0 : onClick(e);
                const copyText = typeof text === 'string' ? text : text();
                copy(copyText);
            }, ref: ref, size: size, ...passThrough })));
});
CopyButton.defaultProps = defaultProps;
CopyButton.displayName = 'CopyButton';

export { CopyButton };
//# sourceMappingURL=CopyButton.esm.js.map
